import React, { useEffect, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { isSearchEnabled } from 'lib/searchUtils'
import HeadData from 'components/Common/HeadData'
import Spacing from 'components/Common/Spacing'
import PartnershipBanner from 'components/Common/Vendors/Partnership/PartnershipBanner'
import { LUXURY_ESCAPES } from 'constants/brands'
import { TYPE_PAGE_LIMIT } from 'constants/search'
import * as InteractionStudioService from 'api/interactionStudio'

import config from 'constants/config'

import { TypesPageContainer } from './styles'
import { fetchListBanners } from 'actions/ContentActions'
import { fetchHotelPopularHolidayTypes } from 'actions/DestinationAlertsActions'
import BannerCarousel from 'components/Common/Banner/BannerCarousel'
import { BANNER_LOCATION_HOTELS_AND_RESORTS } from 'constants/bannerLocations'
import { FLASH_HOTELS_AND_TAO_OFFERS_TYPES, HOTEL_PAGE_OFFER_TYPES } from 'constants/offerList'
import Heading from 'components/Luxkit/Typography/Heading'
import { frontPageSortByTypes } from 'constants/offerListFilters'
import GlobalSearchHeroContainer from 'components/Search/GlobalSearch/GlobalSearchHeroContainer'
import GlobalSearchContainerPane, { GlobalSearchContainerPaneContent } from 'components/Search/GlobalSearch/GlobalSearchContainerPane'
import SearchTrendingDestinations from 'components/Search/SearchForm/SearchTrendingDestinations'
import GlobalHotelsSearchTabContent from 'components/Search/GlobalSearch/GlobalSearchVerticalsTabContents/GlobalHotelsSearchTabContent'
import isAnyFilterApplied from 'lib/filtersUtils'
import GlobalMobileSearchHeroBar from 'components/SearchV2/Components/Mobile/GlobalMobileSearchHeroBar'
import OfferList, { OfferListV2Item } from 'components/OfferList/v2/OfferList'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/v2/OfferListEventsContext'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import { EXPERIMENT, startExperiment } from 'lib/optimize'
import AnywhereFilterTabs from '../HotelSearchPage/AnywhereFilterTabs'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import styled from 'styled-components'
import OfferCountAndSort from '../HotelSearchPage/OfferCountAndSort'
import { rem } from 'polished'
import { fireABTestGAEvent } from 'api/googleTagManager'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import HotelsPageHighIntentHotels from 'components/Pages/TypesPage/HotelsPageHighIntentHotels'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import { Trans, useTranslation } from 'react-i18next'
import TopPicksForMeCarousel from 'components/Recommendations/TopPicksForMe/TopPicksForMeCarousel'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { LE_CONTRACTED_HOTEL_TYPES } from 'constants/offer'

const TabsContainer = styled(LayoutContainer)`
  > * + * {
    padding-top: ${rem(16)};
  }
`

const Wrapper = styled.div`
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.eight};
  padding: ${rem(16)} 0;
`

interface MappedStateProps {
  windowSearch: string;
  partnership: App.Partnership;
  trendingDestinations: App.TrendingDestination[];
  filters: App.OfferListFilters;
  regionCode: string;
  isSearchEnabled: boolean;
  banners: Array<App.ListBanner>;
}

interface MappedDispatchProps {
  fetchListBanners: Utils.ActionDispatcher<typeof fetchListBanners>;
}

const hotelsDestinations = ['Bali', 'Phuket', 'Europe', 'Australia']

function onFilterClick() {
  fireABTestGAEvent({ category: 'hotels-page', action: 'click-on-filter' }, false)
}

function HotelsPage({
  filters,
  partnership,
  regionCode,
  isSearchEnabled,
  banners,
  fetchListBanners,
  trendingDestinations,
  windowSearch,
}: MappedStateProps & MappedDispatchProps) {
  const isManualSortingEnabled = isManualSortingEnabledFor('hotels')
  const { t } = useTranslation(['common', 'search'])
  useEffect(() => {
    fetchListBanners(BANNER_LOCATION_HOTELS_AND_RESORTS)
  }, [fetchListBanners])

  useEffect(() => {
    startExperiment(EXPERIMENT.TRENDING_DESTINATION_BY_SALES)
    const intervalId = setInterval(() => {
      fireABTestGAEvent({ category: 'hotels-page-viewing', action: '15-seconds' }, true)
    }, 15000)
    return () => clearInterval(intervalId)
  }, [])

  const dispatch = useAppDispatch()

  const hasFilters = isAnyFilterApplied(filters)

  const onAnalyticEvent = useCallback((action: OfferListEvents, data: any) => {
    if (action === OfferListEvents.productClick) {
      const { offer, index } = data
      fireABTestGAEvent({ category: 'hotels-page', action: 'click-on-offer' }, false)
      InteractionStudioService.trackOfferListClick({
        offerId: offer.id,
        index,
        list: 'hotels',
      })
    }
  }, [])

  const offerListExtras: Array<OfferListV2Item> = [{
    id: 'title',
    position: 'start',
    element: <Heading variant="heading2" align="center">Hotels & Resorts</Heading>,
    show: !isSearchEnabled,
  }, {
    id: 'partnership',
    position: 'start',
    element: <PartnershipBanner bannerId="types-page" />,
    show: !!partnership && partnership.code !== VIRGIN_VELOCITY_PARTNERSHIP_CODE && regionCode !== 'AU' && regionCode !== 'GB' && regionCode !== 'US',
  }, {
    position: 1,
    id: 'bannerCarousel',
    element: <BannerCarousel banners={banners} />,
    show: !!banners?.length,
  }]

  const headTitle = config.BRAND === LUXURY_ESCAPES ? t('bestPriceOnEarthTagline', { ns: 'common' }) : t('Hotels & Resorts', { ns: 'common' })
  const description = config.BRAND === LUXURY_ESCAPES ? t('exclusiveOffersDescription', { ns: 'common' }) : undefined

  const trendingDestinationsLabels = useMemo(() => (
    trendingDestinations.length ? trendingDestinations.map(destination => destination.primaryText) : hotelsDestinations
  ), [trendingDestinations])

  const finalFilters: App.OfferListFilters = useMemo(() => {
    if (isManualSortingEnabled && !hasFilters && !filters.sortBy) {
      return {
        // Change to ALL_HOTEL_TYPES when the bedbank tile is implemented
        // https://aussiecommerce.atlassian.net/browse/CRO-2605
        offerTypes: LE_CONTRACTED_HOTEL_TYPES,
        sortBy: 'vertical-hotel',
        limit: TYPE_PAGE_LIMIT,
      }
    }

    return {
      ...filters,
      offerTypes: !hasFilters ? HOTEL_PAGE_OFFER_TYPES(regionCode) : FLASH_HOTELS_AND_TAO_OFFERS_TYPES,
      frontPageVisibleOnly: true,
      limit: TYPE_PAGE_LIMIT,
      sortBy: filters.sortBy ?? 'homepage',
    }
  }, [filters, hasFilters, isManualSortingEnabled, regionCode])

  const mapViewURL = '/search/map?adults=2&children=none'

  useEffect(() => {
    dispatch(fetchHotelPopularHolidayTypes())
  }, [dispatch])

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage} >
    <Spacing pb={32}>
      <HeadData title={`${headTitle} - ${config.title} ${regionCode}`} description={description} />
      <ErrorBoundary fallback={null}>
        <PromptCardSelector abandonedCart hotLead tripReengagement />
      </ErrorBoundary>
      {isSearchEnabled && <GlobalSearchHeroContainer
        heroImageId={config.searchFormSection.hotelsImageId}
        title={t('Hotels & Resorts', { ns: 'common' })}
      >
        <GlobalSearchContainerPane displayMobile={!config.USE_INSPIRATIONAL_SEARCH}>
          <GlobalSearchContainerPaneContent>
            <GlobalHotelsSearchTabContent />
          </GlobalSearchContainerPaneContent>
        </GlobalSearchContainerPane>

        {config.USE_INSPIRATIONAL_SEARCH && <GlobalMobileSearchHeroBar
          placeholder={t('Search hotel offers in', { ns: 'search' })}
          placeholderTextList={trendingDestinationsLabels}
        />}

        <SearchTrendingDestinations
          titleColour="neutral-eight"
          titleWeight="bold"
        />
      </GlobalSearchHeroContainer>}
      <HotelsPageHighIntentHotels />
      <TopPicksForMeCarousel trackingPageSource="Hotels Page" />
      <Wrapper>
        <TabsContainer>
          <Heading variant="heading3">
            <Trans i18nKey="offerListFiltersHeading" ns="common">
              Explore today's <i>exclusive</i> offers
            </Trans>
          </Heading>
          <AnywhereFilterTabs filters={finalFilters} search={windowSearch} onFilterClick={onFilterClick} />
        </TabsContainer>
      </Wrapper>
      <TypesPageContainer>
        <OfferCountAndSort
          mainListTotal={0}
          sortOptions={frontPageSortByTypes}
          mapViewUrl={mapViewURL}
          filters={finalFilters}
          windowSearch={windowSearch}
          shouldShowFilters showOfferCount={false}
          showHolidayTypes={false}
        />
        <OfferListEventsProvider onListEvent={onAnalyticEvent}>
          <OfferList
            filters={finalFilters}
            interstitials={offerListExtras}
          />
        </OfferListEventsProvider>
      </TypesPageContainer>
    </Spacing>
  </AnalyticsPageContext.Provider>
}

const mapStateToProps = (state: App.State): MappedStateProps => ({
  windowSearch: state.router.location.search,
  partnership: state.geo.partnership,
  trendingDestinations: state.destination.trendingDestinations,
  filters: getUrlOfferListFilters(state),
  regionCode: state.geo.currentRegionCode,
  isSearchEnabled: isSearchEnabled(),
  banners: state.content.listBanners?.hotelsAndResorts,
})

const mapDispatchToProps: MappedDispatchProps = {
  fetchListBanners,
}

export default connect(mapStateToProps, mapDispatchToProps)(HotelsPage)
